import { useState } from 'react'
import { DangerTriangleIcon } from '@solar-icons/react/linear/danger-triangle'
import { EyeIcon } from '@solar-icons/react/linear/eye'
import { EyeClosedIcon } from '@solar-icons/react/linear/eye-closed'
import { RestartIcon } from '@solar-icons/react/linear/restart'
import {
  Badge,
  Button,
  ConfirmButton,
  Field,
  Input,
  Panel,
  SegmentedControl,
  Toggle,
} from '@/components/ui'
import { QUERY_ROUTE_LABELS, QUERY_ROUTE_PATHS } from '@/lib/api/queryApi'
import type { QueryRoute } from '@/lib/api/queryApi'
import { joinUrl } from '@/lib/api'
import { TIMEOUT_BOUNDS, useSettingsStore } from '@/stores/settingsStore'
import { useHistoryStore } from '@/stores/historyStore'

const LOCALES = [
  { value: 'en-IN', label: 'en-IN' },
  { value: 'en-US', label: 'en-US' },
  { value: 'en-GB', label: 'en-GB' },
]

export function SettingsView() {
  const settings = useSettingsStore()
  const clearHistory = useHistoryStore((state) => state.clear)
  const [showToken, setShowToken] = useState(false)

  const baseUrlValid = Boolean(settings.baseUrl.trim())
  const productionWithoutToken =
    settings.route === 'production' && !settings.bearerToken

  return (
    <div className="scrollbar-thin h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-6">
        <Panel
          title="Connection"
          description="Where this client sends requests. Stored in this browser only."
        >
          <div className="space-y-5">
            <Field
              label="API base URL"
              htmlFor="base-url"
              hint={
                <>
                  Resolved endpoint:{' '}
                  <code className="font-mono text-fg">
                    {baseUrlValid
                      ? joinUrl(settings.baseUrl, QUERY_ROUTE_PATHS[settings.route])
                      : '—'}
                  </code>
                  . Use <code className="font-mono">/api</code> for the Vite dev
                  proxy, or point at the backend origin directly.
                </>
              }
              error={baseUrlValid ? undefined : 'A base URL is required.'}
            >
              <Input
                id="base-url"
                mono
                invalid={!baseUrlValid}
                value={settings.baseUrl}
                spellCheck={false}
                autoComplete="off"
                onChange={(event) => settings.setBaseUrl(event.target.value)}
                placeholder="http://127.0.0.1:8000"
              />
            </Field>

            <Field
              label="Database"
              htmlFor="database-name"
              hint="Optional when one database is connected. Required when multiple databases are connected. Use the database name returned by POST /connections."
            >
              <Input
                id="database-name"
                mono
                value={settings.database}
                spellCheck={false}
                autoComplete="off"
                onChange={(event) => settings.setDatabase(event.target.value)}
                placeholder="RMS_DEV"
              />
            </Field>

            <Field
              label="Query route"
              hint={
                settings.route === 'test'
                  ? 'POST /query/test runs without authentication, and 404s when ENABLE_TEST_ROUTE is false.'
                  : 'POST /query verifies a host-issued JWT via get_access_scope and applies role-based row-level filtering.'
              }
            >
              <SegmentedControl<QueryRoute>
                ariaLabel="Query route"
                value={settings.route}
                onChange={settings.setRoute}
                options={[
                  { value: 'test', label: 'Test', title: QUERY_ROUTE_LABELS.test },
                  {
                    value: 'production',
                    label: 'Production',
                    title: QUERY_ROUTE_LABELS.production,
                  },
                ]}
              />
            </Field>

            <Field
              label="Bearer token"
              htmlFor="bearer-token"
              hint="Sent as an Authorization header on the production route only. Never attached to the test route."
            >
              <div className="flex items-center gap-2">
                <Input
                  id="bearer-token"
                  mono
                  type={showToken ? 'text' : 'password'}
                  value={settings.bearerToken}
                  spellCheck={false}
                  autoComplete="off"
                  onChange={(event) => settings.setBearerToken(event.target.value)}
                  placeholder="eyJhbGciOi..."
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowToken((value) => !value)}
                  aria-label={showToken ? 'Hide token' : 'Show token'}
                  title={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? <EyeClosedIcon size={16} strokeWidth={1.75} /> : <EyeIcon size={16} strokeWidth={1.75} />}
                </Button>
              </div>
            </Field>

            {productionWithoutToken && (
              <div className="flex gap-2.5 rounded-lg border border-warning/30 bg-warning-soft px-3.5 py-2.5">
                <DangerTriangleIcon size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-warning" />
                <p className="text-xs leading-relaxed text-muted">
                  The production route is selected but no token is set, so queries
                  will fail before leaving the browser. Note that the backend
                  helper <code className="font-mono">_load_verification_key</code>{' '}
                  still raises{' '}
                  <code className="font-mono">NotImplementedError</code>, so this
                  route cannot succeed until the host JWT wiring lands.
                </p>
              </div>
            )}

            <Field
              label="Request timeout"
              htmlFor="timeout"
              hint={`Between ${TIMEOUT_BOUNDS.min / 1000}s and ${TIMEOUT_BOUNDS.max / 1000}s. LLM-backed queries can legitimately take a minute or more.`}
            >
              <div className="flex items-center gap-3">
                <Input
                  id="timeout"
                  type="number"
                  className="w-32"
                  min={TIMEOUT_BOUNDS.min / 1000}
                  max={TIMEOUT_BOUNDS.max / 1000}
                  value={Math.round(settings.requestTimeoutMs / 1000)}
                  onChange={(event) =>
                    settings.setRequestTimeoutMs(Number(event.target.value) * 1000)
                  }
                />
                <span className="text-xs text-subtle">seconds</span>
              </div>
            </Field>
          </div>
        </Panel>

        <Panel title="Appearance">
          <div className="space-y-5">
            <Field label="Theme">
              <SegmentedControl
                ariaLabel="Theme"
                value={settings.theme}
                onChange={settings.setTheme}
                options={[
                  { value: 'system', label: 'System' },
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                ]}
              />
            </Field>

            <Field label="Table density">
              <SegmentedControl
                ariaLabel="Table density"
                value={settings.density}
                onChange={settings.setDensity}
                options={[
                  { value: 'comfortable', label: 'Comfortable' },
                  { value: 'compact', label: 'Compact' },
                ]}
              />
            </Field>

            <Field
              label="Number formatting"
              hint="Drives every formatted number and timestamp in results."
            >
              <SegmentedControl
                ariaLabel="Locale"
                value={settings.locale}
                onChange={settings.setLocale}
                options={LOCALES}
              />
            </Field>
          </div>
        </Panel>

        <Panel title="Behaviour">
          <div className="space-y-5">
            <Toggle
              id="auto-scroll"
              label="Follow new results"
              description="Scrolls the thread to the newest exchange, unless you have scrolled up to read something."
              checked={settings.autoScroll}
              onChange={settings.setAutoScroll}
            />
            <Toggle
              id="remember-history"
              label="Keep query history"
              description="Stores every query you run in this browser so you can re-run it from the History view."
              checked={settings.rememberHistory}
              onChange={settings.setRememberHistory}
            />
          </div>
        </Panel>

        <Panel
          title="Reset"
          description="Local data only. Nothing on the server is affected."
        >
          <div className="flex flex-wrap items-center gap-2">
            <ConfirmButton
              onConfirm={settings.reset}
              confirmLabel="Reset all settings?"
              icon={<RestartIcon size={14} strokeWidth={1.75} />}
            >
              Reset settings
            </ConfirmButton>
            <ConfirmButton onConfirm={clearHistory} confirmLabel="Delete all history?">
              Clear history
            </ConfirmButton>
            <Badge className="ml-auto">qap.settings.v1</Badge>
          </div>
        </Panel>
      </div>
    </div>
  )
}
